import { allocateDiscountToTaxBrackets } from "../../../../../../../utils/commonfunction";

interface OrderedItem {
  consumption_tax_rate?: number;
  tax_price?: number;
  price?: number;
  quantity: number;
}

interface SubOrder {
  subtotal?: number;
  tax_price?: number;
  grand_total?: number;
  service_charge?: number;
  ordered_items?: OrderedItem[];
}

interface OrderDetail {
  subtotal?: number;
  tax_price?: number;
  grand_total?: number;
  service_charge?: number;
  ordered_items?: OrderedItem[];
  subOrders?: (SubOrder | null | undefined)[];
  coupon_code?: string;
  coupon_discount?: number;
  coupon_type?: string;
  coupon_code_amount?: number;
  coupon_code_applied?: boolean;
  gift_card_applied?: boolean;
  gift_card_amount?: number;
}

interface OrderPriceDetail {
  subtotal: number;
  tax_price: number;
  grand_total: number;
  total_tax_8: number;
  total_tax_10: number;
  tax_amount_8: number;
  tax_amount_10: number;
  service_charge: number;
  great_grand_total: number;
  amount_after_discount: number;
  coupon_code_applied: boolean;
  coupon_code: string;
  coupon_discount: number | undefined;
  coupon_type: string | undefined;
  coupon_code_amount: number;
  gift_card_applied: boolean;
  gift_card_amount: number;
}

const accumulateTaxByRate = (data: OrderPriceDetail, items: OrderedItem[]): void => {
  items.forEach((item) => {
    const itemTotal = ((item?.tax_price ?? 0) + (item?.price ?? 0)) * item.quantity;
    // total_tax_8/_10 are tax-inclusive bracket totals. tax_amount_* is the tax alone.
    const itemTax = (item?.tax_price ?? 0) * item.quantity;
    if (item.consumption_tax_rate === 8) {
      data.total_tax_8 += itemTotal;
      data.tax_amount_8 += itemTax;
    } else if (item.consumption_tax_rate === 10) {
      data.total_tax_10 += itemTotal;
      data.tax_amount_10 += itemTax;
    }
  });
};

export const calculateOrderPriceDetail = (
  orderDetail: OrderDetail = {},
  couponCodeApplied: boolean = false,
  taxRoundType?: string
): OrderPriceDetail => {
  const data: OrderPriceDetail = {
    subtotal: 0,
    tax_price: 0,
    grand_total: 0,
    total_tax_8: 0,
    total_tax_10: 0,
    tax_amount_8: 0,
    tax_amount_10: 0,
    service_charge: 0,
    great_grand_total: 0,
    amount_after_discount: 0,
    coupon_code_applied: couponCodeApplied,
    coupon_code: orderDetail?.coupon_code ?? "",
    coupon_discount: orderDetail?.coupon_discount,
    coupon_type: orderDetail?.coupon_type,
    coupon_code_amount: orderDetail?.coupon_code_amount ?? 0,
    gift_card_applied: !!orderDetail?.gift_card_applied,
    gift_card_amount: orderDetail?.gift_card_amount ?? 0,
  };

  const subOrders = orderDetail?.subOrders ?? [];

  if (orderDetail?.ordered_items?.length) {
    data.subtotal += orderDetail?.subtotal ?? 0;
    data.tax_price += orderDetail?.tax_price ?? 0;
    data.grand_total += orderDetail?.grand_total ?? 0;
    data.service_charge += orderDetail?.service_charge ?? 0;

    accumulateTaxByRate(data, orderDetail.ordered_items);
  }

  subOrders.forEach((order) => {
    if (!order) return;

    data.subtotal += order?.subtotal ?? 0;
    data.tax_price += order?.tax_price ?? 0;
    data.grand_total += order?.grand_total ?? 0;
    data.service_charge += order?.service_charge ?? 0;

    accumulateTaxByRate(data, order?.ordered_items ?? []);
  });

  data.great_grand_total = data.subtotal + data.service_charge + data.tax_price;
  data.amount_after_discount = data.great_grand_total;

  if (orderDetail?.coupon_code_applied) {
    data.amount_after_discount -= orderDetail?.coupon_code_amount ?? 0;
  }

  // Cap gift card at the remaining (post-coupon) balance so it can't push
  // amount_after_discount negative — port of the WRDS-388 fix that landed in
  // calculateEditSalesTotals but predates this TS refactor's own copy of the
  // calculation.
  if (orderDetail?.gift_card_applied) {
    const cappedGiftCardAmount = Math.min(
      orderDetail?.gift_card_amount ?? 0,
      Math.max(data.amount_after_discount, 0)
    );
    data.gift_card_amount = cappedGiftCardAmount;
    data.amount_after_discount -= cappedGiftCardAmount;
  }

  // Discount lands on the 10% dine-in bracket first, then spills into 8%.
  const brackets = allocateDiscountToTaxBrackets({
    totalTax8: data.total_tax_8,
    totalTax10: data.total_tax_10,
    discount:
      (orderDetail?.coupon_code_applied ? data.coupon_code_amount : 0) +
      (orderDetail?.gift_card_applied ? data.gift_card_amount : 0),
    taxRoundType,
  });

  return { ...data, ...brackets };
};